import { flow, Instance, types } from 'mobx-state-tree';

import { Storage } from '@ionic/storage';

import _ from 'lodash';
import { BluetoothInfoModel } from '../BluetoothInfo';
import { Environment } from '../environment';
import { withEnvironment } from '../extensions/with-environment';
import { RecordListModel } from '../RecordList/RecordList';
import { RunModel } from '../RunModel/RunModel';
import { AuthUser, UserModel, UserSnapshot } from '../User';
import AppDataModel from 'models/AppData';
import { ExploreElementStoreModel } from '../ExploreElementStore/ExploreElementStore';
import { sleep } from '../../util';

const fileStorage = new Storage();

export const initializeStorage = async () => {
  await fileStorage.create();
};

export const RootStoreModel = types
  .model({
    recordList: RecordListModel,
    knownFreak: BluetoothInfoModel,
    currentUser: types.maybe(UserModel),
    appData: AppDataModel,
    exploreElementStore: ExploreElementStoreModel,
    isUserLoading: types.optional(types.boolean, false),
  })
  .extend(withEnvironment)
  .actions((self) => {
    const queryRuns = flow(function* () {
      if (!self.currentUser) {
        return;
      }
      const knownRunIDs = new Set(self.recordList.tricks.map((t) => t.id));
      const knownFriendRuns = new Set(
        self.recordList.friendsRuns.map((t) => t.id)
      );
      const favRuns = new Set(self.currentUser.favoriteRuns);

      const newUserRuns = (
        (yield self.environment.cloudStorage.getRunsByUser(
          self.currentUser.uid
        )) as Array<RunModel>
      ).filter((trick) => !knownRunIDs.has(trick.id));

      const newFriendRuns = (
        (yield self.environment.cloudStorage.getRunsById(
          self.currentUser.friendRuns
        )) as Array<RunModel>
      ).filter((trick) => !knownFriendRuns.has(trick.id));

      const newRunModels = [
        ...self.recordList.pushRuns(newUserRuns),
        ...self.recordList.addFriendRuns(newFriendRuns),
      ];
      newRunModels.forEach((trick) => {
        if (favRuns.has(trick.shareUrl!)) trick.setFavorite(true);
      });
    });

    const setUser = flow(function* (
      usr: AuthUser | null,
      isNew: boolean = false
    ) {
      self.isUserLoading = true;
      try {
        if (!usr) {
          self.currentUser = undefined;
          self.recordList.deleteAll();
          return;
        }

        const oldUID = self.currentUser?.uid;

        const userModel = UserModel.create(
          _.assign(
            {
              realName: usr.displayName,
            } as Partial<UserSnapshot>,
            usr
          ),
          self.environment
        );

        if (isNew) {
          userModel.addFriendRun('RlVgnmmfYXR111m2IYCQ', true);
          userModel.addFavorite('RlVgnmmfYXR111m2IYCQ', true);
          yield sleep(1000);
          yield userModel.putSelf();
        }
        yield userModel.query();
        const mergedUser = _.mergeWith(
          {},
          self.currentUser,
          userModel,
          (ov, sv) => {
            if (sv === undefined || sv === null) return ov;
            return undefined;
          }
        );

        self.currentUser = mergedUser;

        if (self.currentUser && oldUID !== self.currentUser.uid) {
          self.recordList.deleteAll();
        }
        queryRuns();
      } finally {
        self.isUserLoading = false;
      }
    });

    return {
      queryRuns,
      setUser,
      removeKnownFreak() {
        self.knownFreak = BluetoothInfoModel.create({});
        self.currentUser?.freak1.clear();
      },
      reset() {
        self.recordList.deleteAll();
        self.recordList.deleteAllFriendRuns();
        self.knownFreak = BluetoothInfoModel.create({});
      },
    };
  });

export const createEmptyState = (env: Environment, data?: any) => {
  return RootStoreModel.create(
    data ?? {
      recordList: RecordListModel.create({ tricks: [] }),
      knownFreak: {},
      appData: {},
      exploreElementStore: {},
    },
    env
  );
};

// export const rootStore = createEmptyState();
export type RootStore = Instance<typeof RootStoreModel>;
